import { FuneralHomeInfo } from '@shared/api/funeralHomeInfo/funeralHomeInfo.interface';
import { RoomInfo } from '@shared/api/roomInfo/roomInfo.interface';

import { ShopOrderForSeller, ShopOrderSellerRequestStatus } from './shopOrder.interface';

//* PG 자동환불이 안 되는 결제(가상계좌·무통장 계열) 판별 — 면세계산서/수기환불 요청 대상.
//* method는 자유 문자열('카드'·'가상계좌'·'계좌 입금'·레거시 'vbank')이라 키워드 포함으로 판별한다.
export const DEPOSIT_LIKE_METHOD_KEYWORDS = ['가상', '계좌', '입금'] as const;

export const isDepositLikePayment = (paymentDetail?: { method?: string }): boolean => {
  const method = paymentDetail?.method ?? '';
  if (!method) return false;
  if (method.toLowerCase().includes('vbank')) return true;
  return DEPOSIT_LIKE_METHOD_KEYWORDS.some((keyword) => method.includes(keyword));
};

export const sellerRequestStatusLabel: Record<ShopOrderSellerRequestStatus, string> = {
  Requested: '대기',
  Done: '완료',
  Rejected: '반려',
};

//* Bugo.fevent 타입(Fevent 제네릭 기본값)은 funeralHomeInfo·roomInfo ref를 string으로 두지만,
//* 셀러 목록·과거배송검색 쿼리가 populate하므로 런타임엔 객체가 온다. 미populate면 undefined.
const asPopulated = function <T>(value: T | string | undefined): T | undefined {
  return typeof value === 'object' && value !== null ? value : undefined;
};

//* 부고(fevent) 기준 현재 상가 주소 — 주문 생성 시 receiverAddress 조합식과 동일
//* (ShopItemCheckoutPage 참조). 호실이동으로 fevent가 갱신됐다면 새 호실이 나온다.
//* bugo 없음(전화주문 등)·주소 정보 미populate면 undefined.
export const feventCurrentAddress = (
  shopOrder: ShopOrderForSeller,
): string | undefined => {
  const fevent = shopOrder.bugo?.fevent;
  if (!fevent) return undefined;
  const funeralHome = asPopulated<FuneralHomeInfo>(fevent.funeralHomeInfo);
  const room = asPopulated<RoomInfo>(fevent.roomInfo);
  const address = fevent.funeralHomeInfoEmbed?.address ?? funeralHome?.address;
  if (!address) return undefined;
  const homeName = funeralHome?.name ?? fevent.funeralHomeInfoEmbed?.name ?? '';
  const roomName = fevent.roomInfoEmbed?.name ?? room?.name ?? '';
  return `${address}, [${homeName}, ${roomName}]`;
};

//* 쉼표·공백 등 표기 차이만 다른 주소를 같다고 보기 위한 정규화
//* (구 조합식은 괄호 앞 ', ' 구분자가 없어 "…(독곡동)[제일장례식장, 202호실]" 형태로 저장됨)
const normalizeAddress = (address: string): string => address.replace(/[\s,]/g, '');

//* 부고 기준 현재 상가 주소와 주문에 저장된 배송지 주소의 불일치 여부 (호실이동 감지)
export const feventAddressMismatch = (shopOrder: ShopOrderForSeller): boolean => {
  const feventAddress = feventCurrentAddress(shopOrder);
  return (
    feventAddress !== undefined &&
    normalizeAddress(feventAddress) !==
      normalizeAddress(shopOrder.deliveryDetail.receiverAddress)
  );
};

export const orderContentString = (shopOrder: ShopOrderForSeller) => {
  return (
    `${shopOrder.orderDetail.shopItemEmbed.name}\n\n` +
    `주소: ${shopOrder.deliveryDetail.receiverAddress}\n` +
    `문구(보내는분): ${shopOrder.bugoDetail.senderPhrase}\n` +
    `조의문구: ${shopOrder.bugoDetail.condolencePhrase}\n` +
    `받으시는분: ${shopOrder.deliveryDetail.receiverName} 상주님\n` +
    `도매가: ${shopOrder.orderDetail.shopItemEmbed.priceWhole.toLocaleString()}원`
  );
};
